import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Save,
  Refresh,
  Visibility,
  VisibilityOff,
  Link as LinkIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Broker logos (these would be actual image imports in a real app)
const brokerLogos = {
  interactiveBrokers: "IB",
  tdAmeritrade: "TD",
  alpaca: "ALP",
  robinhood: "RH",
  etrade: "ETR",
  alphaVantage: "AV",
  yahooFinance: "YF",
  polygon: "POL",
  finnhub: "FIN",
  openai: "OAI",
  huggingface: "HF",
  binance: "BIN",
  coinbase: "CB",
  kraken: "KRK",
  kucoin: "KUC",
  twitter: "X",
  tradingview: "TV",
  anthropic: "ANT",
  google: "GOO",
  meta: "META",
  cohere: "COH",
  mistral: "MIS",
  ai21: "AI21",
  nvidia: "NV",
  reddit: "RED",
  stocktwits: "STW",
  linkedin: "LI",
  youtube: "YT",
  bloomberg: "BLM",
  newsapi: "NEWS",
  financialModelingPrep: "FMP",
  secEdgar: "SEC",
  whaleAlert: "WHL",
  glassnode: "GLS",
  santiment: "SNT",
  nansen: "NAN",
  arkhamIntelligence: "ARK",
  cryptoQuant: "CRQ",
  intoTheBlock: "ITB",
  coinbaseCloud: "CBC",
  duneAnalytics: "DUN",
  etherscan: "ETH",
};

// Mock API connection data
const initialApiConnections = {
  socialMedia: [
    {
      id: "twitter",
      name: "X (Twitter)",
      logo: brokerLogos.twitter,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accessToken: "",
      accessTokenSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "X API v2",
      website: "https://developer.x.com",
      pricing: "paid",
      pricingDetails: "$100/mo Basic, $5000/mo Pro",
      dataFilters: {
        keywords: "",
        hashtags: "",
        accounts: "",
        sentimentAnalysis: true,
      },
    },
    {
      id: "reddit",
      name: "Reddit",
      logo: brokerLogos.reddit,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accessToken: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "Reddit API (OAuth2)",
      website: "https://www.reddit.com/dev/api",
      pricing: "free",
      pricingDetails: "Free tier available",
      dataFilters: {
        subreddits: "",
        keywords: "",
        timeRange: "day",
        sentimentAnalysis: true,
      },
    },
    {
      id: "stocktwits",
      name: "StockTwits",
      logo: brokerLogos.stocktwits,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "StockTwits API v2",
      website: "https://api.stocktwits.com/developers",
      pricing: "free",
      pricingDetails: "Free tier available",
      dataFilters: {
        symbols: "",
        trending: true,
        sentimentAnalysis: true,
      },
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      logo: brokerLogos.linkedin,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accessToken: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "LinkedIn API v2",
      website: "https://developer.linkedin.com",
      pricing: "paid",
      pricingDetails: "Enterprise only",
      dataFilters: {
        companies: "",
        keywords: "",
        industries: "",
      },
    },
    {
      id: "youtube",
      name: "YouTube",
      logo: brokerLogos.youtube,
      status: "disconnected",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "YouTube Data API v3",
      website: "https://developers.google.com/youtube/v3",
      pricing: "free",
      pricingDetails: "Free tier with quota limits",
      dataFilters: {
        channels: "",
        keywords: "",
        categories: "business,finance",
      },
    },
    {
      id: "bloomberg",
      name: "Bloomberg",
      logo: brokerLogos.bloomberg,
      status: "disconnected",
      apiKey: "",
      tier: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "Bloomberg API (Enterprise)",
      website: "https://www.bloomberg.com/professional/support/api-library/",
      pricing: "paid",
      pricingDetails: "Enterprise $20k+/year",
      dataFilters: {
        topics: "markets,economy,technology",
        regions: "global",
        industries: "",
      },
    },
    {
      id: "newsapi",
      name: "News API",
      logo: brokerLogos.newsapi,
      status: "disconnected",
      apiKey: "",
      tier: "Free",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "News API v2",
      website: "https://newsapi.org/docs",
      pricing: "freemium",
      pricingDetails: "Free for dev, $449/mo production",
      dataFilters: {
        sources: "",
        categories: "business,technology",
        keywords: "",
        countries: "us,gb,ca",
      },
    },
  ],
  brokers: [
    {
      id: "interactive-brokers",
      name: "Interactive Brokers",
      logo: brokerLogos.interactiveBrokers,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      apiSecret: "****************************************",
      accountId: "U123456789",
      paperTrading: true,
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      apiVersion: "Client Portal API / TWS API",
      website: "https://www.interactivebrokers.com/en/trading/ib-api.php",
      pricing: "free",
      pricingDetails: "Free API with brokerage account",
      commission: "$",
      commissionDetails: "$0.005/share (min $1), Options $0.65/contract",
    },
    {
      id: "schwab",
      name: "Charles Schwab",
      logo: brokerLogos.tdAmeritrade,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accountId: "",
      paperTrading: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "Schwab API (2024)",
      website: "https://developer.schwab.com",
      pricing: "free",
      pricingDetails: "Free API with brokerage account",
      commission: "$",
      commissionDetails: "$0 stocks/ETFs, Options $0.65/contract",
      note: "TD Ameritrade migrated to Schwab in 2024",
    },
    {
      id: "alpaca",
      name: "Alpaca",
      logo: brokerLogos.alpaca,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      apiSecret: "****************************************",
      accountId: "ALPACA12345",
      paperTrading: true,
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      apiVersion: "Trading API v2 (OMS v2)",
      website: "https://docs.alpaca.markets",
      pricing: "free",
      pricingDetails: "Free API, commission-free trading",
      commission: "$",
      commissionDetails: "$0 commission on stocks",
    },
    {
      id: "robinhood",
      name: "Robinhood",
      logo: brokerLogos.robinhood,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accountId: "",
      paperTrading: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "No official API",
      pricing: "unavailable",
      pricingDetails: "No public API available",
      commission: "$",
      commissionDetails: "$0 commission (no API)",
      note: "Robinhood does not offer a public trading API",
    },
    {
      id: "etrade",
      name: "E*TRADE",
      logo: brokerLogos.etrade,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accountId: "",
      paperTrading: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "E*TRADE API v1",
      website: "https://developer.etrade.com",
      pricing: "free",
      pricingDetails: "Free API with brokerage account",
      commission: "$",
      commissionDetails: "$0 stocks/ETFs, Options $0.65/contract",
    },
  ],
  marketData: [
    {
      id: "tradingview",
      name: "TradingView Webhook",
      logo: brokerLogos.tradingview,
      status: "disconnected",
      webhookUrl: "",
      webhookSecret: "",
      lastUpdated: null,
      nodeIntegrated: false,
      chartIntegration: true,
      alertNotifications: true,
      indicatorSignals: true,
      autoTrading: false,
      apiVersion: "Webhook API",
      website: "https://www.tradingview.com/support/solutions/43000529348",
      pricing: "freemium",
      pricingDetails: "Free webhooks, $14.95/mo+ for alerts",
    },
    {
      id: "alpha-vantage",
      name: "Alpha Vantage",
      logo: brokerLogos.alphaVantage,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      tier: "Premium",
      rateLimits: "150 calls/min",
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      apiVersion: "REST API (2025)",
      website: "https://www.alphavantage.co/documentation/",
      pricing: "freemium",
      pricingDetails: "Free 25/day, $50/mo premium",
    },
    {
      id: "yahoo-finance",
      name: "Yahoo Finance",
      logo: brokerLogos.yahooFinance,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      tier: "Basic",
      rateLimits: "100 calls/hour",
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      apiVersion: "RapidAPI (unofficial)",
      website: "https://rapidapi.com/sparior/api/yahoo-finance15",
      pricing: "freemium",
      pricingDetails: "Free tier via RapidAPI",
      note: "Unofficial API - may have reliability issues",
    },
    {
      id: "polygon",
      name: "Massive (Polygon.io)",
      logo: brokerLogos.polygon,
      status: "disconnected",
      apiKey: "",
      tier: "",
      rateLimits: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "REST API v3",
      website: "https://polygon.io",
      pricing: "freemium",
      pricingDetails: "Free tier, $29-$199/mo",
      note: "Rebranded from Polygon.io to Massive.com in 2025",
    },
    {
      id: "finnhub",
      name: "Finnhub",
      logo: brokerLogos.finnhub,
      status: "disconnected",
      apiKey: "",
      tier: "",
      rateLimits: "",
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "REST API v1",
      website: "https://finnhub.io/docs/api",
      pricing: "freemium",
      pricingDetails: "Free tier, $50/mo+",
    },
    {
      id: "financial-modeling-prep",
      name: "Financial Modeling Prep",
      logo: brokerLogos.financialModelingPrep,
      status: "disconnected",
      apiKey: "",
      tier: "",
      rateLimits: "",
      lastConnected: null,
      nodeIntegrated: false,
      website: "https://financialmodelingprep.com/developer/docs",
      pricing: "freemium",
      pricingDetails: "Free 250/day, $19/mo+",
      features: {
        financialStatements: true,
        stockScreener: true,
        companyValuation: true,
        economicData: true,
      },
    },
    {
      id: "sec-edgar",
      name: "SEC EDGAR",
      logo: brokerLogos.secEdgar,
      status: "disconnected",
      apiKey: "",
      email: "",
      lastConnected: null,
      nodeIntegrated: false,
      website: "https://www.sec.gov/developer",
      pricing: "free",
      pricingDetails: "Free government data",
      features: {
        filings: true,
        formTypes: "10-K,10-Q,8-K,13F",
        realTimeAlerts: false,
        textAnalysis: true,
      },
    },
  ],
  aiServices: [
    {
      id: "openai",
      name: "OpenAI",
      logo: brokerLogos.openai,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      website: "https://platform.openai.com",
      pricing: "paid",
      pricingDetails: "Pay per token, ~$0.01-$0.06/1K tokens",
      models: [
        "gpt-5.2",
        "gpt-5.2-pro",
        "gpt-5",
        "gpt-5-mini",
        "gpt-5-nano",
        "gpt-4.1",
        "gpt-4o",
        "gpt-4o-mini",
        "o4-mini",
        "o3",
        "gpt-image-1.5",
        "text-embedding-3-large",
        "text-embedding-3-small",
        "gpt-4o-transcribe",
        "gpt-4o-mini-tts",
      ],
      selectedModel: "gpt-4.1",
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      trainingEnabled: true,
      useCase: "market analysis, strategy development, news interpretation",
      apiVersion: "v1 (2025)",
    },
    {
      id: "anthropic",
      name: "Anthropic Claude",
      logo: brokerLogos.anthropic,
      status: "disconnected",
      apiKey: "",
      website: "https://console.anthropic.com",
      pricing: "paid",
      pricingDetails: "Pay per token, ~$0.003-$0.015/1K tokens",
      models: [
        "claude-opus-4.5",
        "claude-sonnet-4",
        "claude-sonnet-3.7",
        "claude-3.5-sonnet",
        "claude-3.5-haiku",
        "claude-3-opus",
        "claude-3-sonnet",
        "claude-3-haiku",
      ],
      selectedModel: "claude-sonnet-4",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "financial document analysis, risk assessment",
      apiVersion: "2024-10-22",
    },
    {
      id: "google",
      name: "Google AI (Gemini)",
      logo: brokerLogos.google,
      status: "disconnected",
      apiKey: "",
      website: "https://ai.google.dev",
      pricing: "freemium",
      pricingDetails: "Free tier, pay per token after",
      models: [
        "gemini-3-pro",
        "gemini-3-flash",
        "gemini-2.5-pro",
        "gemini-2.5-flash",
        "gemini-2.5-flash-lite",
        "gemini-2.0-flash",
        "gemini-1.5-pro",
        "gemini-1.5-flash",
        "text-embedding-005",
      ],
      selectedModel: "gemini-2.5-flash",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "multi-modal analysis, chart pattern recognition",
      apiVersion: "v1beta (2025)",
    },
    {
      id: "meta",
      name: "Meta AI (Llama)",
      logo: brokerLogos.meta,
      status: "disconnected",
      apiKey: "",
      website: "https://llama.meta.com",
      pricing: "free",
      pricingDetails: "Open source (via providers)",
      models: [
        "llama-4-405b",
        "llama-4-70b",
        "llama-4-8b",
        "llama-3.3-70b",
        "llama-3.2-90b-vision",
        "llama-3.2-11b-vision",
        "llama-3.1-405b",
        "llama-3.1-70b",
        "llama-3.1-8b",
      ],
      selectedModel: "llama-4-70b",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "strategy backtesting, sentiment analysis",
      apiVersion: "via providers (2025)",
    },
    {
      id: "cohere",
      name: "Cohere",
      logo: brokerLogos.cohere,
      status: "disconnected",
      apiKey: "",
      website: "https://cohere.com",
      pricing: "freemium",
      pricingDetails: "Free tier, pay per token",
      models: [
        "command-a-03-2025",
        "command-r-plus-08-2024",
        "command-r-08-2024",
        "command-r7b",
        "command-a-vision",
        "command-a-reasoning",
        "embed-english-v3.0",
        "embed-multilingual-v3.0",
        "rerank-v3.5",
      ],
      selectedModel: "command-a-03-2025",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "semantic search over financial data, document summarization",
      apiVersion: "v2 (2025)",
    },
    {
      id: "mistral",
      name: "Mistral AI",
      logo: brokerLogos.mistral,
      status: "disconnected",
      apiKey: "",
      website: "https://console.mistral.ai",
      pricing: "freemium",
      pricingDetails: "Free tier, pay per token",
      models: [
        "mistral-large-3",
        "mistral-medium-3.1",
        "mistral-small-3.2",
        "ministral-3-14b",
        "ministral-3-8b",
        "ministral-3-3b",
        "magistral-medium-1.2",
        "magistral-small-1.2",
        "codestral",
        "devstral-2",
        "mistral-embed",
      ],
      selectedModel: "mistral-large-3",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "efficient market analysis, pattern detection",
      apiVersion: "v1 (2025)",
    },
    {
      id: "ai21",
      name: "AI21 Labs (Jamba)",
      logo: brokerLogos.ai21,
      status: "disconnected",
      apiKey: "",
      website: "https://studio.ai21.com",
      pricing: "freemium",
      pricingDetails: "Free tier, pay per token",
      models: [
        "jamba-1.5-large",
        "jamba-1.5-mini",
        "jamba-instruct",
        "j2-ultra",
        "j2-mid",
      ],
      selectedModel: "jamba-1.5-large",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "financial report analysis, numerical reasoning",
      apiVersion: "v1 (2025)",
    },
    {
      id: "huggingface",
      name: "HuggingFace",
      logo: brokerLogos.huggingface,
      status: "disconnected",
      apiKey: "",
      website: "https://huggingface.co",
      pricing: "freemium",
      pricingDetails: "Free tier, $9/mo Pro",
      models: [
        "Qwen2.5-72B-Instruct",
        "Qwen2.5-Coder-32B",
        "DeepSeek-V3",
        "Mixtral-8x22B-Instruct",
        "Llama-3.3-70B-Instruct",
        "Phi-4",
        "falcon-180b",
        "finbert-tone",
      ],
      selectedModel: "Qwen2.5-72B-Instruct",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase: "custom model deployment, specialized financial models",
      apiVersion: "Inference API v2",
    },
    {
      id: "nvidia",
      name: "NVIDIA NIM",
      logo: brokerLogos.nvidia,
      status: "disconnected",
      apiKey: "",
      website: "https://build.nvidia.com",
      pricing: "freemium",
      pricingDetails: "Free tier available",
      models: [
        "llama-3.3-70b-instruct",
        "llama-3.1-nemotron-70b-instruct",
        "nemotron-4-340b-instruct",
        "mistral-large-3-instruct",
        "arctic-embed-l",
      ],
      selectedModel: "llama-3.3-70b-instruct",
      lastConnected: null,
      nodeIntegrated: false,
      trainingEnabled: false,
      useCase:
        "high-performance financial modeling, GPU-accelerated trading strategies",
      apiVersion: "NIM API (2025)",
    },
  ],
  cryptoExchanges: [
    {
      id: "binance",
      name: "Binance",
      logo: brokerLogos.binance,
      status: "connected",
      apiKey: "[DEMO_KEY_NOT_REAL]",
      apiSecret: "****************************************",
      accountId: "BIN123456",
      tradingEnabled: true,
      lastConnected: "2026-01-16",
      nodeIntegrated: true,
      apiVersion: "Spot API v3",
      features: ["spot", "margin", "futures", "websocket"],
      website: "https://developers.binance.com",
      pricing: "free",
      pricingDetails: "Free API with exchange account",
      commission: "$",
      commissionDetails: "0.1% maker/taker (lower with BNB)",
    },
    {
      id: "coinbase",
      name: "Coinbase",
      logo: brokerLogos.coinbase,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accountId: "",
      tradingEnabled: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "Advanced Trade API (CDP)",
      features: ["spot", "advanced-trade", "websocket"],
      website: "https://docs.cdp.coinbase.com",
      pricing: "free",
      pricingDetails: "Free API with exchange account",
      commission: "$$",
      commissionDetails: "0.4-0.6% maker/taker",
      note: "Requires CDP API keys (legacy keys expired Feb 2025)",
    },
    {
      id: "kraken",
      name: "Kraken",
      logo: brokerLogos.kraken,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      accountId: "",
      tradingEnabled: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "REST API v0",
      features: ["spot", "margin", "futures", "websocket", "xStocks"],
      website: "https://docs.kraken.com",
      pricing: "free",
      pricingDetails: "Free API with exchange account",
      commission: "$$",
      commissionDetails: "0.16-0.26% maker/taker",
    },
    {
      id: "kucoin",
      name: "KuCoin",
      logo: brokerLogos.kucoin,
      status: "disconnected",
      // Spot Trading API
      spotApiKey: "",
      spotApiSecret: "",
      spotPassphrase: "",
      spotEnabled: false,
      // Margin Trading API
      marginApiKey: "",
      marginApiSecret: "",
      marginPassphrase: "",
      marginEnabled: false,
      // Futures Trading API
      futuresApiKey: "",
      futuresApiSecret: "",
      futuresPassphrase: "",
      futuresEnabled: false,
      // General settings
      accountId: "",
      tradingEnabled: false,
      lastConnected: null,
      nodeIntegrated: false,
      apiVersion: "REST API v2",
      features: ["spot", "margin", "futures", "websocket", "lending"],
      website: "https://docs.kucoin.com",
      pricing: "free",
      pricingDetails: "Free API with exchange account",
      commission: "$",
      commissionDetails: "0.1% maker/taker (lower with KCS)",
      note: "KuCoin requires separate API keys for Spot, Margin, and Futures trading",
      // Default trade type
      defaultTradeType: "spot",
    },
  ],
  cryptoAnalytics: [
    {
      id: "whaleAlert",
      name: "Whale Alert",
      logo: brokerLogos.whaleAlert,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Track large cryptocurrency transactions",
      website: "https://whale-alert.io",
      pricing: "freemium",
      pricingDetails: "Free 10/min, $79/mo+",
    },
    {
      id: "glassnode",
      name: "Glassnode",
      logo: brokerLogos.glassnode,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "On-chain market intelligence",
      website: "https://glassnode.com",
      pricing: "freemium",
      pricingDetails: "Free tier, $29/mo+",
    },
    {
      id: "santiment",
      name: "Santiment",
      logo: brokerLogos.santiment,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Crypto-specific data feeds and analytics",
      website: "https://api.santiment.net",
      pricing: "freemium",
      pricingDetails: "Free tier, $49/mo+",
    },
    {
      id: "nansen",
      name: "Nansen",
      logo: brokerLogos.nansen,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Blockchain analytics platform",
      website: "https://www.nansen.ai",
      pricing: "paid",
      pricingDetails: "$150/mo+",
    },
    {
      id: "arkhamIntelligence",
      name: "Arkham Intelligence",
      logo: brokerLogos.arkhamIntelligence,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Blockchain intelligence for wallets and entities",
      website: "https://www.arkhamintelligence.com",
      pricing: "freemium",
      pricingDetails: "Free tier available",
    },
    {
      id: "cryptoQuant",
      name: "CryptoQuant",
      logo: brokerLogos.cryptoQuant,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "On-chain data analytics platform",
      website: "https://cryptoquant.com",
      pricing: "freemium",
      pricingDetails: "Free tier, $49/mo+",
    },
    {
      id: "intoTheBlock",
      name: "IntoTheBlock",
      logo: brokerLogos.intoTheBlock,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Crypto market intelligence",
      website: "https://app.intotheblock.com",
      pricing: "freemium",
      pricingDetails: "Free tier, $10/mo+",
    },
    {
      id: "coinbaseCloud",
      name: "Coinbase Cloud (TokenAnalyst)",
      logo: brokerLogos.coinbaseCloud,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Blockchain data infrastructure",
      website: "https://www.coinbase.com/cloud",
      pricing: "paid",
      pricingDetails: "Enterprise pricing",
    },
    {
      id: "duneAnalytics",
      name: "Dune Analytics",
      logo: brokerLogos.duneAnalytics,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "SQL-based crypto analytics platform",
      website: "https://dune.com",
      pricing: "freemium",
      pricingDetails: "Free tier, $349/mo+",
    },
    {
      id: "etherscan",
      name: "Etherscan",
      logo: brokerLogos.etherscan,
      status: "disconnected",
      apiKey: "",
      apiSecret: "",
      lastConnected: null,
      nodeIntegrated: false,
      description: "Ethereum blockchain explorer and analytics",
      website: "https://etherscan.io/apis",
      pricing: "freemium",
      pricingDetails: "Free 5/sec, $199/mo+",
    },
  ],
};

const API_BASE = process.env.REACT_APP_API_URL || "https://strategy-engine.houman-ghavamzadeh.workers.dev";

function ApiConnections() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [apiConnections, setApiConnections] = useState(initialApiConnections);
  const [editingApiId, setEditingApiId] = useState(null);
  const [editingApiType, setEditingApiType] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved connections from backend on mount
  const loadSavedConnections = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/connections/`);
      
      // Handle 404 gracefully - endpoint may not be set up yet
      if (!response.ok) {
        console.log("API connections endpoint not available, using defaults");
        setIsLoading(false);
        return;
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // Merge saved connections with initial connections
        const merged = { ...initialApiConnections };
        
        Object.keys(result.data).forEach((type) => {
          if (merged[type]) {
            merged[type] = merged[type].map((initial) => {
              const saved = result.data[type].find((s) => s.id === initial.id);
              if (saved) {
                return {
                  ...initial,
                  status: saved.status || "disconnected",
                  lastConnected: saved.lastConnected,
                  tradingEnabled: saved.tradingEnabled,
                  nodeIntegrated: saved.nodeIntegrated,
                  paperTrading: saved.paperTrading,
                  // Note: credentials are not returned for security
                };
              }
              return initial;
            });
          }
        });
        
        setApiConnections(merged);
      }
    } catch (error) {
      console.log("Could not load saved connections, using defaults");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSavedConnections();
  }, [loadSavedConnections]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const startEditApi = (type, id) => {
    setEditingApiType(type);
    setEditingApiId(id);
  };

  const handleApiInputChange = (event, type, id, field) => {
    const value = event.target.value;

    const updatedConnections = {
      ...apiConnections,
      [type]: apiConnections[type].map((conn) =>
        conn.id === id ? { ...conn, [field]: value } : conn,
      ),
    };

    setApiConnections(updatedConnections);
  };

  const handleToggleChange = async (type, id, field) => {
    const api = apiConnections[type].find((conn) => conn.id === id);
    const newValue = !api[field];
    
    // Update local state immediately for responsiveness
    const updatedConnections = {
      ...apiConnections,
      [type]: apiConnections[type].map((conn) =>
        conn.id === id ? { ...conn, [field]: newValue } : conn,
      ),
    };
    setApiConnections(updatedConnections);

    // If toggling nodeIntegrated and connection is saved, update backend
    if (field === "nodeIntegrated" && api.status === "connected") {
      try {
        const response = await fetch(`${API_BASE}/api/connections/${type}/${id}/toggle-integration`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nodeIntegrated: newValue }),
        });
        
        const result = await response.json();
        
        if (result.success) {
          setAlertMessage({
            type: "success",
            text: newValue 
              ? `${api.name} integrated with nodes system` 
              : `${api.name} removed from nodes system`,
          });
        } else {
          // Revert on failure
          setApiConnections({
            ...apiConnections,
            [type]: apiConnections[type].map((conn) =>
              conn.id === id ? { ...conn, [field]: !newValue } : conn,
            ),
          });
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error toggling integration:", error);
        setAlertMessage({
          type: "error",
          text: `Failed to update integration: ${error.message}`,
        });
      }
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const handleSaveApi = async (type, id) => {
    const api = apiConnections[type].find((api) => api.id === id);

    // KuCoin has multiple API types - check if at least one is configured
    let hasRequiredFields = false;
    if (id === "kucoin") {
      hasRequiredFields = 
        (api.spotApiKey && api.spotApiSecret && api.spotPassphrase) ||
        (api.marginApiKey && api.marginApiSecret && api.marginPassphrase) ||
        (api.futuresApiKey && api.futuresApiSecret && api.futuresPassphrase);
    } else {
      hasRequiredFields =
        api.apiKey &&
        (type === "marketData" ||
          type === "aiServices" ||
          (api.apiSecret && api.accountId));
    }

    if (!hasRequiredFields) {
      setAlertMessage({
        type: "error",
        text: id === "kucoin" 
          ? "Please configure at least one trading type (Spot, Margin, or Futures) with API Key, Secret, and Passphrase"
          : "Please fill in all required fields",
      });
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    try {
      // Build credentials object based on provider
      let credentials = {};
      if (id === "kucoin") {
        credentials = {
          // Spot Trading
          spotApiKey: api.spotApiKey,
          spotApiSecret: api.spotApiSecret,
          spotPassphrase: api.spotPassphrase,
          spotEnabled: api.spotEnabled,
          // Margin Trading
          marginApiKey: api.marginApiKey,
          marginApiSecret: api.marginApiSecret,
          marginPassphrase: api.marginPassphrase,
          marginEnabled: api.marginEnabled,
          // Futures Trading
          futuresApiKey: api.futuresApiKey,
          futuresApiSecret: api.futuresApiSecret,
          futuresPassphrase: api.futuresPassphrase,
          futuresEnabled: api.futuresEnabled,
          // Default trade type
          defaultTradeType: api.defaultTradeType || "spot",
        };
      } else {
        credentials = {
          apiKey: api.apiKey,
          apiSecret: api.apiSecret,
          passphrase: api.passphrase,
          accessToken: api.accessToken,
          accessTokenSecret: api.accessTokenSecret,
        };
      }

      const response = await fetch(`${API_BASE}/api/connections/${type}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          providerName: api.name,
          ...credentials,
          accountId: api.accountId,
          tradingEnabled: api.tradingEnabled,
          nodeIntegrated: api.nodeIntegrated,
          paperTrading: api.paperTrading,
          settings: api.dataFilters || {},
        }),
      });

      const result = await response.json();

      if (result.success) {
        const updatedConnections = {
          ...apiConnections,
          [type]: apiConnections[type].map((conn) =>
            conn.id === id
              ? {
                  ...conn,
                  status: "connected",
                  lastConnected: new Date().toISOString().split("T")[0],
                }
              : conn,
          ),
        };

        setApiConnections(updatedConnections);
        setAlertMessage({
          type: "success",
          text: `${api.name} connection saved successfully!`,
        });
      } else {
        throw new Error(result.error || "Failed to save connection");
      }
    } catch (error) {
      console.error("Error saving API connection:", error);
      setAlertMessage({
        type: "error",
        text: `Failed to save: ${error.message}`,
      });
    }

    setTimeout(() => setAlertMessage(null), 3000);
    setEditingApiId(null);
    setEditingApiType(null);
  };

  const handleTestConnection = async (type, id) => {
    const api = apiConnections[type].find((api) => api.id === id);

    setTestingConnection(true);

    try {
      // Collect the API credentials for testing
      const credentials = {
        apiKey: api.apiKey,
        apiSecret: api.apiSecret,
        accessToken: api.accessToken,
        accessTokenSecret: api.accessTokenSecret,
        webhookUrl: api.webhookUrl,
        webhookSecret: api.webhookSecret,
        accountId: api.accountId,
        email: api.email,
      };

      // Map the type to the format expected by the node system
      const connectionType =
        type === "socialMedia"
          ? "social_media"
          : type === "marketData"
            ? "market_data"
            : type === "brokers"
              ? "broker"
              : type === "aiServices"
                ? "ai_service"
                : type === "cryptoExchanges"
                  ? "crypto_exchange"
                  : type;

      let result;

      // If we're in the Strategy Builder, use the exposed function
      if (window.testStrategyApiConnection) {
        console.log(
          `Testing ${connectionType} connection to ${api.name} (${api.id})`,
        );
        result = await window.testStrategyApiConnection(
          connectionType,
          api.id,
          credentials,
        );
      } else {
        // Fallback to local simulation
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const success = Math.random() > 0.2; // 80% success rate for demo
        result = {
          success: success,
          message: success
            ? `Connection to ${api.name} successful!`
            : `Failed to connect to ${api.name}. Please check your credentials.`,
        };
      }

      // Update UI based on test result
      setAlertMessage({
        type: result.success ? "success" : "error",
        text: result.message,
      });

      // If success, update the connection readiness status for node system
      if (result.success) {
        const updatedConnections = {
          ...apiConnections,
          [type]: apiConnections[type].map((conn) =>
            conn.id === id
              ? {
                  ...conn,
                  nodeIntegrated: true,
                  testSuccess: true,
                  lastTested: new Date().toISOString().split("T")[0],
                }
              : conn,
          ),
        };

        setApiConnections(updatedConnections);
      }
    } catch (error) {
      console.error(`Error testing connection:`, error);
      setAlertMessage({
        type: "error",
        text: `Error testing connection: ${error.message || "Unknown error"}`,
      });
    } finally {
      setTestingConnection(false);
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const renderApiForm = (type, api) => {
    const isEditing = editingApiId === api.id && editingApiType === type;
    const isConnected = api.status === "connected";

    // Different forms based on API type
    const commonFields = (
      <>
        <Grid item xs={12}>
          <TextField
            label="API Key"
            value={api.apiKey}
            onChange={(e) => handleApiInputChange(e, type, api.id, "apiKey")}
            fullWidth
            variant="outlined"
            disabled={!isEditing}
            type={showSecrets[`${type}-${api.id}-key`] ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowSecrets({
                        ...showSecrets,
                        [`${type}-${api.id}-key`]:
                          !showSecrets[`${type}-${api.id}-key`],
                      })
                    }
                    edge="end"
                  >
                    {showSecrets[`${type}-${api.id}-key`] ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </>
    );

    // Type-specific fields
    let typeSpecificFields = null;

    if (type === "marketData" && api.id === "tradingview") {
      typeSpecificFields = (
        <>
          <Grid item xs={12}>
            <TextField
              label="Webhook URL"
              value={api.webhookUrl || ""}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "webhookUrl")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              helperText="Your unique webhook URL that TradingView will send data to"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Webhook Secret"
              value={api.webhookSecret || ""}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "webhookSecret")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              type={
                showSecrets[`${type}-${api.id}-secret`] ? "text" : "password"
              }
              helperText="Secret key for webhook authentication"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSecrets({
                          ...showSecrets,
                          [`${type}-${api.id}-secret`]:
                            !showSecrets[`${type}-${api.id}-secret`],
                        })
                      }
                      edge="end"
                    >
                      {showSecrets[`${type}-${api.id}-secret`] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.chartIntegration}
                  onChange={() =>
                    handleToggleChange(type, api.id, "chartIntegration")
                  }
                  disabled={!isEditing}
                />
              }
              label="Chart Integration"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.alertNotifications}
                  onChange={() =>
                    handleToggleChange(type, api.id, "alertNotifications")
                  }
                  disabled={!isEditing}
                />
              }
              label="Alert Notifications"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.indicatorSignals}
                  onChange={() =>
                    handleToggleChange(type, api.id, "indicatorSignals")
                  }
                  disabled={!isEditing}
                />
              }
              label="Indicator Signals"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.autoTrading}
                  onChange={() =>
                    handleToggleChange(type, api.id, "autoTrading")
                  }
                  disabled={!isEditing}
                />
              }
              label="Automated Trading"
            />
          </Grid>
        </>
      );
    } else if (type === "socialMedia") {
      typeSpecificFields = (
        <>
          <Grid item xs={12}>
            <TextField
              label="API Secret"
              value={api.apiSecret || ""}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "apiSecret")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              type={
                showSecrets[`${type}-${api.id}-secret`] ? "text" : "password"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSecrets({
                          ...showSecrets,
                          [`${type}-${api.id}-secret`]:
                            !showSecrets[`${type}-${api.id}-secret`],
                        })
                      }
                      edge="end"
                    >
                      {showSecrets[`${type}-${api.id}-secret`] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Access Token"
              value={api.accessToken || ""}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "accessToken")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              type={
                showSecrets[`${type}-${api.id}-token`] ? "text" : "password"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSecrets({
                          ...showSecrets,
                          [`${type}-${api.id}-token`]:
                            !showSecrets[`${type}-${api.id}-token`],
                        })
                      }
                      edge="end"
                    >
                      {showSecrets[`${type}-${api.id}-token`] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Access Token Secret"
              value={api.accessTokenSecret || ""}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "accessTokenSecret")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              type={
                showSecrets[`${type}-${api.id}-tokenSecret`]
                  ? "text"
                  : "password"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSecrets({
                          ...showSecrets,
                          [`${type}-${api.id}-tokenSecret`]:
                            !showSecrets[`${type}-${api.id}-tokenSecret`],
                        })
                      }
                      edge="end"
                    >
                      {showSecrets[`${type}-${api.id}-tokenSecret`] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Keywords (comma separated)"
              value={api.dataFilters?.keywords || ""}
              onChange={(e) =>
                handleApiInputChange(
                  { target: { value: e.target.value } },
                  type,
                  api.id,
                  "dataFilters",
                  { ...api.dataFilters, keywords: e.target.value },
                )
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              placeholder="crypto, bitcoin, trading, finance"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Hashtags (comma separated)"
              value={api.dataFilters?.hashtags || ""}
              onChange={(e) =>
                handleApiInputChange(
                  { target: { value: e.target.value } },
                  type,
                  api.id,
                  "dataFilters",
                  { ...api.dataFilters, hashtags: e.target.value },
                )
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              placeholder="#crypto, #trading, #finance"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Accounts to track (comma separated)"
              value={api.dataFilters?.accounts || ""}
              onChange={(e) =>
                handleApiInputChange(
                  { target: { value: e.target.value } },
                  type,
                  api.id,
                  "dataFilters",
                  { ...api.dataFilters, accounts: e.target.value },
                )
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              placeholder="@elonmusk, @jimcramer, @cnbc"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.dataFilters?.sentimentAnalysis || false}
                  onChange={() => {
                    const updatedFilters = {
                      ...api.dataFilters,
                      sentimentAnalysis: !api.dataFilters?.sentimentAnalysis,
                    };
                    handleApiInputChange(
                      { target: { value: updatedFilters } },
                      type,
                      api.id,
                      "dataFilters",
                      updatedFilters,
                    );
                  }}
                  disabled={!isEditing}
                />
              }
              label="Enable Sentiment Analysis"
            />
          </Grid>
        </>
      );
    } else if (type === "brokers" || type === "cryptoExchanges") {
      typeSpecificFields = (
        <>
          <Grid item xs={12}>
            <TextField
              label="API Secret"
              value={api.apiSecret}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "apiSecret")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              type={
                showSecrets[`${type}-${api.id}-secret`] ? "text" : "password"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSecrets({
                          ...showSecrets,
                          [`${type}-${api.id}-secret`]:
                            !showSecrets[`${type}-${api.id}-secret`],
                        })
                      }
                      edge="end"
                    >
                      {showSecrets[`${type}-${api.id}-secret`] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Account ID"
              value={api.accountId}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "accountId")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing}
            />
          </Grid>
          {api.id === "kucoin" && (
            <>
              {/* Show saved indicator if connected */}
              {api.status === "connected" && !isEditing && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>✅ API credentials saved securely.</strong> Credentials are encrypted and not displayed for security.
                      Click "Edit" to update your API keys.
                    </Typography>
                  </Alert>
                </Grid>
              )}
              
              {/* Spot Trading API */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'success.main', fontWeight: 'bold' }}>
                  📊 Spot Trading API {api.spotEnabled && api.status === "connected" && "✅"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Spot API Key"
                  value={api.spotApiKey || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "spotApiKey")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-spotKey`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-spotKey`]: !showSecrets[`${type}-${api.id}-spotKey`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-spotKey`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Spot API Secret"
                  value={api.spotApiSecret || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "spotApiSecret")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-spotSecret`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-spotSecret`]: !showSecrets[`${type}-${api.id}-spotSecret`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-spotSecret`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Spot Passphrase"
                  value={api.spotPassphrase || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "spotPassphrase")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-spotPass`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-spotPass`]: !showSecrets[`${type}-${api.id}-spotPass`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-spotPass`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={api.spotEnabled || false} onChange={() => handleToggleChange(type, api.id, "spotEnabled")} disabled={!isEditing} />}
                  label="Enable Spot Trading"
                />
              </Grid>

              {/* Margin Trading API */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'warning.main', fontWeight: 'bold' }}>
                  📈 Margin Trading API
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Margin API Key"
                  value={api.marginApiKey || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "marginApiKey")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-marginKey`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-marginKey`]: !showSecrets[`${type}-${api.id}-marginKey`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-marginKey`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Margin API Secret"
                  value={api.marginApiSecret || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "marginApiSecret")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-marginSecret`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-marginSecret`]: !showSecrets[`${type}-${api.id}-marginSecret`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-marginSecret`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Margin Passphrase"
                  value={api.marginPassphrase || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "marginPassphrase")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-marginPass`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-marginPass`]: !showSecrets[`${type}-${api.id}-marginPass`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-marginPass`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={api.marginEnabled || false} onChange={() => handleToggleChange(type, api.id, "marginEnabled")} disabled={!isEditing} />}
                  label="Enable Margin Trading"
                />
              </Grid>

              {/* Futures Trading API */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'error.main', fontWeight: 'bold' }}>
                  🚀 Futures Trading API
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Futures API Key"
                  value={api.futuresApiKey || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "futuresApiKey")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-futuresKey`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-futuresKey`]: !showSecrets[`${type}-${api.id}-futuresKey`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-futuresKey`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Futures API Secret"
                  value={api.futuresApiSecret || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "futuresApiSecret")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-futuresSecret`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-futuresSecret`]: !showSecrets[`${type}-${api.id}-futuresSecret`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-futuresSecret`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Futures Passphrase"
                  value={api.futuresPassphrase || ""}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "futuresPassphrase")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  type={showSecrets[`${type}-${api.id}-futuresPass`] ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecrets({ ...showSecrets, [`${type}-${api.id}-futuresPass`]: !showSecrets[`${type}-${api.id}-futuresPass`] })} edge="end">
                          {showSecrets[`${type}-${api.id}-futuresPass`] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={api.futuresEnabled || false} onChange={() => handleToggleChange(type, api.id, "futuresEnabled")} disabled={!isEditing} />}
                  label="Enable Futures Trading"
                />
              </Grid>

              {/* Default Trade Type */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'info.main', fontWeight: 'bold' }}>
                  ⚙️ Default Settings
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Default Trade Type"
                  value={api.defaultTradeType || "spot"}
                  onChange={(e) => handleApiInputChange(e, type, api.id, "defaultTradeType")}
                  fullWidth
                  variant="outlined"
                  disabled={!isEditing}
                  SelectProps={{ native: true }}
                  helperText="Select which API to use by default for trading"
                >
                  <option value="spot">Spot Trading</option>
                  <option value="margin">Margin Trading</option>
                  <option value="futures">Futures Trading</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> KuCoin requires separate API keys for each trading type. 
                    Create different API keys on KuCoin with the appropriate permissions:
                    <br />• <strong>Spot</strong>: Enable "Spot Trading" permission
                    <br />• <strong>Margin</strong>: Enable "Margin Trading" permission
                    <br />• <strong>Futures</strong>: Enable "Futures Trading" permission
                  </Typography>
                </Alert>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    type === "brokers" ? api.paperTrading : api.tradingEnabled
                  }
                  onChange={() =>
                    handleToggleChange(
                      type,
                      api.id,
                      type === "brokers" ? "paperTrading" : "tradingEnabled",
                    )
                  }
                  disabled={!isEditing}
                />
              }
              label={type === "brokers" ? "Paper Trading" : "Trading Enabled"}
            />
          </Grid>
        </>
      );
    } else if (type === "aiServices") {
      typeSpecificFields = (
        <>
          <Grid item xs={12}>
            <TextField
              select
              label="Model"
              value={api.selectedModel}
              onChange={(e) =>
                handleApiInputChange(e, type, api.id, "selectedModel")
              }
              fullWidth
              variant="outlined"
              disabled={!isEditing || api.models.length === 0}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select a model</option>
              {api.models.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </TextField>
          </Grid>
          {api.useCase && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Recommended Use Case:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {api.useCase}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.trainingEnabled}
                  onChange={() =>
                    handleToggleChange(type, api.id, "trainingEnabled")
                  }
                  disabled={!isEditing}
                />
              }
              label="Enable Training on Nodes"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={api.nodeIntegrated}
                  onChange={() =>
                    handleToggleChange(type, api.id, "nodeIntegrated")
                  }
                  disabled={!isEditing || !isConnected}
                />
              }
              label="Connect to Trading Nodes"
            />
          </Grid>
        </>
      );
    } else if (type === "marketData") {
      typeSpecificFields = (
        <>
          <Grid item xs={12}>
            <TextField
              select
              label="Tier"
              value={api.tier}
              onChange={(e) => handleApiInputChange(e, type, api.id, "tier")}
              fullWidth
              variant="outlined"
              disabled={!isEditing}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select tier</option>
              <option value="Free">Free</option>
              <option value="Basic">Basic</option>
              <option value="Premium">Premium</option>
              <option value="Enterprise">Enterprise</option>
            </TextField>
          </Grid>
        </>
      );
    }

    // Node integration settings - common for all
    const nodeIntegrationSettings = (
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={api.nodeIntegrated}
              onChange={() =>
                handleToggleChange(type, api.id, "nodeIntegrated")
              }
              disabled={!isEditing || !isConnected}
            />
          }
          label="Integrate with Nodes System"
        />
        {isConnected && api.nodeIntegrated && (
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            This API is integrated with your nodes system for trading and
            analytics.
          </Typography>
        )}
      </Grid>
    );

    return (
      <Grid container spacing={2}>
        {commonFields}
        {typeSpecificFields}
        {nodeIntegrationSettings}

        <Grid
          item
          xs={12}
          sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}
        >
          {!isEditing ? (
            <Button
              variant="contained"
              onClick={() => startEditApi(type, api.id)}
              startIcon={<SettingsIcon />}
            >
              Configure
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditingApiId(null);
                  setEditingApiType(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSaveApi(type, api.id)}
                startIcon={<Save />}
              >
                Save
              </Button>
            </Box>
          )}

          {isConnected && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleTestConnection(type, api.id)}
              startIcon={
                testingConnection ? <CircularProgress size={20} /> : <Refresh />
              }
              disabled={testingConnection || isEditing}
            >
              Test Connection
            </Button>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderApiCards = (type) => {
    const apis = apiConnections[type] || [];

    return (
      <Grid container spacing={3}>
        {apis.map((api) => (
          <Grid item xs={12} md={6} key={api.id}>
            <Card variant="outlined">
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      bgcolor:
                        api.status === "connected"
                          ? "success.main"
                          : "grey.400",
                    }}
                  >
                    {api.logo}
                  </Avatar>
                }
                title={
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                    <Typography variant="h6">{api.name}</Typography>
                    <Chip
                      size="small"
                      label={
                        api.status === "connected"
                          ? "Connected"
                          : "Disconnected"
                      }
                      color={api.status === "connected" ? "success" : "default"}
                      sx={{ ml: 1 }}
                    />
                    {api.pricing && (
                      <Chip
                        size="small"
                        label={api.pricing === "free" ? "FREE" : api.pricing === "freemium" ? "FREEMIUM" : api.pricing === "paid" ? "PAID" : "N/A"}
                        color={api.pricing === "free" ? "success" : api.pricing === "freemium" ? "warning" : api.pricing === "paid" ? "error" : "default"}
                        sx={{ ml: 0.5, fontWeight: "bold" }}
                      />
                    )}
                  </Box>
                }
                subheader={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {api.status === "connected"
                        ? `Last connected: ${api.lastConnected}`
                        : "Not configured"}
                    </Typography>
                    {api.pricingDetails && (
                      <Typography variant="caption" color="text.secondary">
                        💰 {api.pricingDetails}
                      </Typography>
                    )}
                    {api.commission && (
                      <Typography variant="caption" display="block" sx={{ color: api.commission === '$' ? 'success.main' : api.commission === '$$' ? 'warning.main' : 'error.main' }}>
                        {api.commission === '$' ? '💵 Cheap' : api.commission === '$$' ? '💵💵 Moderate' : '💵💵💵 Expensive'}: {api.commissionDetails}
                      </Typography>
                    )}
                    {api.website && (
                      <Typography variant="caption" display="block">
                        <a 
                          href={api.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                          🔗 API Documentation
                        </a>
                      </Typography>
                    )}
                  </Box>
                }
              />
              <CardContent>{renderApiForm(type, api)}</CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">API Connections</Typography>
        <Button
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={() => navigate("/user/strategy-builder")}
        >
          Go to Strategy Builder
        </Button>
      </Box>

      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          sx={{ mb: 3 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="API Connection Tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Social Media" />
          <Tab label="Market Data" />
          <Tab label="Brokers" />
          <Tab label="AI Services" />
          <Tab label="Crypto Exchanges" />
          <Tab label="Crypto Analytics" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {tabValue === 0 && renderApiCards("socialMedia")}
        {tabValue === 1 && renderApiCards("marketData")}
        {tabValue === 2 && renderApiCards("brokers")}
        {tabValue === 3 && renderApiCards("aiServices")}
        {tabValue === 4 && renderApiCards("cryptoExchanges")}
        {tabValue === 5 && renderApiCards("cryptoAnalytics")}
      </Box>
    </Box>
  );
}

export default ApiConnections;
